import React from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";

import { Formik } from "formik";
import * as Yup from "yup";

import RegionAutoComplete from "./RegionAutoComplete";
import RegionalManagerAutoComplete from "./RegionalManagerAutoComplete";
import SalesManagerAutocomplete from "./SalesManagerAutocomplete";
import AreaAutoComplete from "./AreaAutoComplete";
import AreaSalesManagerAutocomplete from "./AreaSalesManagerAutocomplete";
import SeniorExecutiveAutocomplete from "./SeniorExecutiveAutocomplete";

// ===============================
// COMPONENT
// ===============================

export default function AddUpdateAllSeg({
  showModal,
  handleCloseModal,
  handleSubmit,
  btnLoading,
  initialData,
  type,
}) {
  // ===============================
  // VALIDATION
  // ===============================

  const validationSchema = Yup.object().shape({
    regionId: Yup.number()
      .required(`field is required`)
      .moreThan(0, `field is required`),

    firstName: Yup.string().trim().required("First name is required"),

    lastName: Yup.string().trim().required("Last name is required"),

    userName: Yup.string().trim().required("Username is required"),

    email: Yup.string()
      .trim()
      .email("Invalid email")
      .required("Email is required"),

    phoneNumber: Yup.string().required("Phone number is required"),

    address: Yup.string().trim().required("Address is required"),

    password: Yup.string().when("id", {
      is: (id) => id === 0,
      then: (schema) =>
        schema
          .required("Password is required")
          .min(6, "Password must be at least 6 characters")
          .max(10, "Maximum 10 characters allowed")
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=(?:.*[^A-Za-z0-9]){2,}).{6,10}$/,
            "Password must contain uppercase, lowercase, number and at least 2 special characters",
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {initialData.id !== 0 ? `Update ${type}` : `Add ${type}`}
        </Modal.Title>
      </Modal.Header>

      <Formik
        enableReinitialize
        initialValues={initialData}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                {/* REGION / PARENT */}

                <Col md={values.id === 0 ? 4 : 6}>
                  <Form.Group>
                    <Form.Label>
                      {type === "Regional Manager" && "Region"}
                      {type === "Sales Manager" && "Regional Manager"}
                      {type === "Area Sales Manager" && "Sales Manager"}
                      {type === "Senior Executive" && "Area Sales Manager"}
                      {type === "Sales Person" && "Senior Executive"}
                    </Form.Label>

                    {/* REGION */}
                    {type === "Regional Manager" && (
                      <RegionAutoComplete
                        value={values.region}
                        onChange={(selected) => {
                          setFieldValue("regionId", selected?.id || 0);
                          setFieldValue("region", selected || null);
                        }}
                      />
                    )}

                    {/* REGIONAL MANAGER */}
                    {type === "Sales Manager" && (
                      <RegionalManagerAutoComplete
                        value={values.region}
                        onChange={(selected) => {
                          setFieldValue("regionId", selected?.regionId || 0);
                          setFieldValue("region", selected || null);
                        }}
                      />
                    )}

                    {/* SALES MANAGER */}
                    {type === "Area Sales Manager" && (
                      <SalesManagerAutocomplete
                        value={values.parent}
                        onChange={(selected) => {
                          setFieldValue("regionId", selected?.id || 0);
                          setFieldValue("parent", selected || null);
                        }}
                      />
                    )}
                    {type === "Senior Executive" && (
                      <AreaSalesManagerAutocomplete
                        value={values.region}
                        onChange={(selected) => {
                          setFieldValue("regionId", selected?.id || 0);
                          setFieldValue("region", selected || null);
                        }}
                      />
                    )}
                    {type === "Sales Person" && (
                      <SeniorExecutiveAutocomplete
                        value={values.region}
                        onChange={(selected) => {
                          setFieldValue("regionId", selected?.id || 0);
                          setFieldValue("region", selected || null);
                        }}
                      />
                    )}

                    {touched.regionId && errors.regionId && (
                      <div className="text-danger small mt-1">
                        {errors.regionId}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* USER NAME */}

                <Col md={values.id === 0 ? 4 : 6}>
                  <Form.Group>
                    <Form.Label>User Name</Form.Label>

                    <Form.Control
                      type="text"
                      name="userName"
                      value={values.userName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.userName && errors.userName}
                      placeholder="User Name"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.userName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* PASSWORD */}

                {values.id === 0 && (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Password</Form.Label>

                      <Form.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && errors.password}
                        placeholder="Password"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}

                {/* AREA */}

                {type === "Area Sales Manager" && (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Area</Form.Label>

                      <AreaAutoComplete
                        value={values.area}
                        onChange={(selected) => {
                          setFieldValue("area", selected || null);
                        }}
                      />

                      {touched.area && errors.area && (
                        <div className="text-danger small mt-1">
                          {errors.area}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}

                {/* FIRST NAME */}

                <Col
                  md={
                    type === "Area Sales Manager" || type === "Sales Person"
                      ? 4
                      : 6
                  }
                >
                  <Form.Group>
                    <Form.Label>First Name</Form.Label>

                    <Form.Control
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.firstName && errors.firstName}
                      placeholder="First Name"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* LAST NAME */}

                <Col
                  md={
                    type === "Area Sales Manager" || type === "Sales Person"
                      ? 4
                      : 6
                  }
                >
                  <Form.Group>
                    <Form.Label>Last Name</Form.Label>

                    <Form.Control
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.lastName && errors.lastName}
                      placeholder="Last Name"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {type === "Sales Person" && (
                  <Col
                    md={
                      type === "Area Sales Manager" || type === "Sales Person"
                        ? 4
                        : 6
                    }
                  >
                    <Form.Group>
                      <Form.Label>Target</Form.Label>

                      <Form.Control
                        type="number"
                        name="target"
                        value={values.target}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.target && errors.target}
                        placeholder="Target"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.target}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
                {/* EMAIL */}

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>

                    <Form.Control
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && errors.email}
                      placeholder="Email"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* PHONE NUMBER */}

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Phone Number</Form.Label>

                    <Form.Control
                      type="text"
                      name="phoneNumber"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.phoneNumber && errors.phoneNumber}
                      placeholder="Phone Number"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.phoneNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* ADDRESS */}

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Address</Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.address && errors.address}
                      placeholder="Address"
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>

              <Button type="submit" variant="primary" disabled={btnLoading}>
                {btnLoading
                  ? "Please Wait..."
                  : values.id !== 0
                    ? "Update"
                    : "Add"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
